<script>
import multiLeadsAPI from 'dashboard/api/integrations/multileads';
// import { required, minValue } from 'vuelidate/lib/validators';
import { useVuelidate } from '@vuelidate/core';
import { required, minValue } from '@vuelidate/validators';
import WootModalHeader from 'dashboard/components/ModalHeader.vue';
import WootInput from 'dashboard/components/widgets/forms/Input.vue';
import V3Textarea from 'v3/components/Form/Textarea.vue';
import V3Select from 'v3/components/Form/Select.vue';

export default {
  components: {
    WootModalHeader,
    WootInput,
    V3Textarea,
    V3Select,
  },
  props: {
    show: Boolean,
    contactId: { type: Number, required: true },
    inboxId: { type: Number, required: true },
    tenantData: { type: Object, required: true },
    conversationId: { type: Number, required: true },
  },
  validations: {
    newLead: {
      title: {
        required,
      },
      lead_type_id: {
        required,
      },
      lead_value: {
        minValue: minValue(0),
      },
      pipeline_id: {
        required,
      },
    },
  },
  emits: ['leadCreated', 'creationError', 'close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isSubmitting: false,
      isFetchingUsers: false,
      isFetchingLeadTypes: false,
      isFetchingGroups: false,
      isFetchingPipelines: false,
      departmentSelectionMade: false,
      responsibleUsers: [],
      leadTypes: [],
      groups: [],
      pipelines: [],
      newLead: {
        title: '',
        description: '',
        lead_value: '',
        temperature: '4',
        user_id: null,
        lead_type_id: null,
        group_id: null,
        pipeline_id: null,
        lead_pipeline_stage_id: null,
      },
    };
  },
  computed: {
    isSaveButtonDisabled() {
      return this.v$.newLead.$invalid || this.isSubmitting;
    },
    leadTypesOptions() {
      return this.leadTypes.map(type => ({
        value: type.id,
        label: type.name,
      }));
    },
    pipelinesOptions() {
      return this.pipelines.map(pipeline => ({
        value: pipeline.id,
        label: pipeline.name,
      }));
    },
    groupsOptions() {
      const groupItems = this.groups.map(group => ({
        value: group.id,
        label: group.name,
      }));
      return [
        {
          value: null,
          label: this.$t(
            'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.NO_DEPARTMENT'
          ),
        },
        ...groupItems,
      ];
    },
    temperatureOptions() {
      return [
        {
          value: '1',
          label: this.$t('CONVERSATION.MULTILEADS.TEMPERATURE_LABELS.COLD'),
        },
        {
          value: '2',
          label: this.$t('CONVERSATION.MULTILEADS.TEMPERATURE_LABELS.WARM'),
        },
        {
          value: '3',
          label: this.$t('CONVERSATION.MULTILEADS.TEMPERATURE_LABELS.HOT'),
        },
        {
          value: '4',
          label: this.$t('CONVERSATION.MULTILEADS.TEMPERATURE_LABELS.NONE'),
        },
      ];
    },
    responsibleUsersOptions() {
      let filteredUsers = this.responsibleUsers;
      const selectedGroupId = this.newLead.group_id;

      if (selectedGroupId) {
        filteredUsers = this.responsibleUsers.filter(user =>
          user.tenants?.some(tenant =>
            tenant.groups?.some(group => group.id === Number(selectedGroupId))
          )
        );
      }
      const users = filteredUsers.map(user => ({
        value: user.id,
        label: user.name,
      }));

      return [
        {
          value: null,
          label: this.$t(
            'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.NO_RESPONSIBLE'
          ),
        },
        ...users,
      ];
    },
  },
  watch: {
    'newLead.group_id'(newGroupId) {
      if (!this.newLead.user_id) {
        return;
      }

      if (newGroupId) {
        const selectedUser = this.responsibleUsers.find(
          user => user.id === this.newLead.user_id
        );

        const userIsInNewGroup = selectedUser?.tenants?.some(tenant =>
          tenant.groups?.some(group => group.id === Number(newGroupId))
        );

        if (!userIsInNewGroup) {
          this.newLead.user_id = null;
        }
      }
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.fetchResponsibleUsers();
      this.fetchLeadTypes();
      this.fetchGroups();
      this.fetchPipelines();
    },
    async fetchResponsibleUsers() {
      if (!this.tenantData || !this.tenantData.id) return;
      this.isFetchingUsers = true;
      try {
        const response = await multiLeadsAPI.fetchUserData(this.tenantData.id);
        this.responsibleUsers = response.data || [];
      } catch (error) {
        this.responsibleUsers = [];
      } finally {
        this.isFetchingUsers = false;
      }
    },
    async fetchLeadTypes() {
      if (!this.tenantData || !this.tenantData.id) return;
      this.isFetchingLeadTypes = true;
      try {
        const response = await multiLeadsAPI.getLeadTypes(this.tenantData.id);
        this.leadTypes = response.data || [];
      } catch (error) {
        this.leadTypes = [];
      } finally {
        this.isFetchingLeadTypes = false;
      }
    },
    async fetchGroups() {
      if (!this.tenantData || !this.tenantData.id) return;
      this.isFetchingGroups = true;
      try {
        const response = await multiLeadsAPI.fetchGroupData(this.tenantData.id);
        this.groups = response.data || [];
      } catch (error) {
        this.groups = [];
      } finally {
        this.isFetchingGroups = false;
      }
    },
    async fetchPipelines() {
      if (!this.tenantData || !this.tenantData.id) return;
      this.isFetchingPipelines = true;
      try {
        const response = await multiLeadsAPI.fetchPipelines(this.tenantData.id);
        this.pipelines = response.data || [];
      } catch (error) {
        this.pipelines = [];
      } finally {
        this.isFetchingPipelines = false;
      }
    },
    async submitLead() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.isSubmitting = true;
      try {
        const leadPayload = { ...this.newLead };
        if (leadPayload.lead_value) {
          leadPayload.lead_value = Number(leadPayload.lead_value);
        }

        if (leadPayload.user_id === null || leadPayload.user_id === '') {
          delete leadPayload.user_id;
        }

        if (leadPayload.group_id === null || leadPayload.group_id === '') {
          delete leadPayload.group_id;
        }

        const selectedPipeline = this.pipelines.find(
          p => p.id === Number(leadPayload.pipeline_id)
        );

        leadPayload.lead_pipeline_stage_id =
          selectedPipeline.lead_pipeline_stage_id;

        const payload = {
          contact_id: this.contactId,
          inbox_id: this.inboxId,
          lead: leadPayload,
          conversation_id: this.conversationId,
        };

        await multiLeadsAPI.createLead(payload);
        this.$emit('leadCreated');
      } catch (error) {
        const specificErrorMessage = error?.response?.data?.error;
        const genericErrorMessage = this.$t(
          'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.ANNOUNCEMENT.FAIL'
        );

        const finalMessage = specificErrorMessage
          ? `${genericErrorMessage}: ${specificErrorMessage}`
          : genericErrorMessage;

        this.$emit('creationError', finalMessage);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <transition name="modal-fade">
    <div v-if="show" class="modal-mask" @mousedown="$emit('close')">
      <div
        class="modal-container rounded-xl w-[48rem] rtl:text-right shadow-md max-h-full overflow-auto relative bg-white dark:bg-slate-800 skip-context-menu"
        @mousedown.stop
      >
        <woot-button
          color-scheme="secondary"
          icon="dismiss"
          variant="clear"
          class="absolute z-10 ltr:right-2 rtl:left-2 top-2"
          @click="$emit('close')"
        />
        <div class="w-full">
          <WootModalHeader
            :header-title="$t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.TITLE')"
            :header-content="
              $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.DESCRIPTION_HEADER')
            "
          />

          <div class="p-4 max-h-[70vh] overflow-y-auto">
            <form class="space-y-4" @submit.prevent="submitLead">
              <WootInput
                v-model.trim="newLead.title"
                :class="{ error: v$.newLead.title.$error }"
                :label="
                  $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.LEAD_TITLE')
                "
                :placeholder="
                  $t(
                    'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.PLACEHOLDERS.LEAD_TITLE'
                  )
                "
                :error="
                  v$.newLead.title.$error
                    ? $t(
                        'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.ERRORS.TITLE_REQUIRED'
                      )
                    : ''
                "
                @blur="v$.newLead.title.$touch"
              />

              <V3Textarea
                v-model="newLead.description"
                :label="
                  $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.DESCRIPTION')
                "
                name="description"
                :placeholder="
                  $t(
                    'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.PLACEHOLDERS.DESCRIPTION'
                  )
                "
              />

              <WootInput
                v-model="newLead.lead_value"
                type="number"
                min="0"
                :class="{ error: v$.newLead.lead_value.$error }"
                :label="$t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.VALUE')"
                :placeholder="
                  $t(
                    'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.PLACEHOLDERS.VALUE'
                  )
                "
                :error="
                  v$.newLead.lead_value.$error
                    ? $t(
                        'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.ERRORS.VALUE_CANNOT_BE_NEGATIVE'
                      )
                    : ''
                "
                @blur="v$.newLead.lead_value.$touch"
              />

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div @focusout="v$.newLead.lead_type_id.$touch">
                  <V3Select
                    v-model="newLead.lead_type_id"
                    :class="{ error: v$.newLead.lead_type_id.$error }"
                    :label="
                      $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.LEAD_TYPE')
                    "
                    name="lead_type_id"
                    :placeholder="
                      isFetchingLeadTypes
                        ? $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.LOADING')
                        : $t(
                            'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.SELECT_TYPE'
                          )
                    "
                    :has-error="v$.newLead.lead_type_id.$error"
                    :error-message="
                      v$.newLead.lead_type_id.$error
                        ? $t(
                            'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.ERRORS.LEAD_TYPE_REQUIRED'
                          )
                        : ''
                    "
                    :options="leadTypesOptions"
                    @input="v$.newLead.lead_type_id.$touch"
                  />
                </div>

                <div @focusout="v$.newLead.pipeline_id.$touch">
                  <V3Select
                    v-model="newLead.pipeline_id"
                    :class="{ error: v$.newLead.pipeline_id.$error }"
                    :label="
                      $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.PIPELINE')
                    "
                    name="pipeline_id"
                    :placeholder="
                      isFetchingPipelines
                        ? $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.LOADING')
                        : $t(
                            'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.SELECT_PIPELINE'
                          )
                    "
                    :has-error="v$.newLead.pipeline_id.$error"
                    :error-message="
                      v$.newLead.pipeline_id.$error
                        ? $t(
                            'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.ERRORS.PIPELINE_REQUIRED'
                          )
                        : ''
                    "
                    :options="pipelinesOptions"
                    @input="v$.newLead.pipeline_id.$touch"
                  />
                </div>

                <V3Select
                  v-model="newLead.temperature"
                  :label="
                    $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.TEMPERATURE')
                  "
                  name="temperature"
                  :placeholder="
                    $t('CONVERSATION.MULTILEADS.TEMPERATURE_LABELS.NONE')
                  "
                  :options="temperatureOptions"
                />

                <V3Select
                  v-model="newLead.group_id"
                  :label="
                    $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.DEPARTMENT')
                  "
                  name="group_id"
                  :placeholder="
                    isFetchingGroups
                      ? $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.LOADING')
                      : $t(
                          'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.SELECT_DEPARTMENT'
                        )
                  "
                  :options="groupsOptions"
                  @input="departmentSelectionMade = true"
                />

                <V3Select
                  v-model="newLead.user_id"
                  :label="
                    $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.RESPONSIBLE')
                  "
                  name="user_id"
                  :placeholder="
                    isFetchingUsers
                      ? $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.LOADING')
                      : $t(
                          'CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.SELECT_RESPONSIBLE'
                        )
                  "
                  :options="responsibleUsersOptions"
                  :disabled="!departmentSelectionMade"
                />
              </div>

              <div class="flex justify-end gap-2 pt-4">
                <woot-button
                  variant="clear"
                  type="button"
                  @click="$emit('close')"
                >
                  {{ $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.CANCEL') }}
                </woot-button>
                <woot-button
                  type="submit"
                  :is-loading="isSubmitting"
                  :disabled="isSaveButtonDisabled"
                >
                  {{ $t('CONVERSATION.MULTILEADS.CREATE_LEAD_FORM.SAVE') }}
                </woot-button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.modal-mask {
  @apply flex items-center justify-center bg-modal-backdrop-light dark:bg-modal-backdrop-dark z-[9991] h-full left-0 fixed top-0 w-full;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
  @apply opacity-0;
}
</style>
